// ScheduleViewingDialog — host for the appointment form opened by the
// "Đặt lịch xem" CTA on the property-detail page. Renders as a `Dialog`
// on desktop (centered, max-w-[520px]) and a bottom `Sheet` on mobile
// (90vh max-height). The form itself lives in `AppointmentForm`; this
// wrapper owns the chrome (header strip + custom close button) and
// the open/close plumbing.

import { X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { AppointmentForm } from "@/features/appointment/components/appointment-form";
import type { Property } from "@/types/property";

interface ScheduleViewingDialogProps {
  property: Property;
  open: boolean;
  onClose: () => void;
  isDesktop: boolean;
}

export function ScheduleViewingDialog({
  property,
  open,
  onClose,
  isDesktop,
}: ScheduleViewingDialogProps) {
  if (!open) return null;

  const header = (
    <div className="flex items-center justify-between border-b border-border px-5 py-4">
      <div>
        <p className="font-mono text-[12px] text-muted-foreground">
          {property.internalCode !== null ? `BĐS · ${property.internalCode}` : "BĐS"}
        </p>
        <div className="mt-0.5 text-[16px] font-bold tracking-tight">
          Yêu cầu xem nhà
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Đóng"
        className="flex size-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
      >
        <X aria-hidden="true" className="size-3.5" />
      </button>
    </div>
  );

  const body = (
    <div className="px-5 py-4">
      <AppointmentForm
        mode="create"
        prefill={{ propertyId: property.id }}
        onSuccess={onClose}
        onCancel={onClose}
      />
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <DialogContent
          className="gap-0 overflow-hidden p-0 sm:max-w-[520px]"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">Yêu cầu xem nhà</DialogTitle>
          {header}
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent
        side="bottom"
        className="max-h-[90vh] gap-0 overflow-y-auto p-0"
        showCloseButton={false}
      >
        <SheetTitle className="sr-only">Yêu cầu xem nhà</SheetTitle>
        {header}
        {body}
      </SheetContent>
    </Sheet>
  );
}
